<template>
  <div id="budget-index">
    <div id="page-head">
      <ul id="breadcrumbs">
        <li>
          <router-link :to="{ name: 'Dashboard' }">Dashboard</router-link>
        </li>
        <li>Budget Manager</li>
      </ul>
    </div>

    <div class="panel">
      <div class="panel-head">
        <h2>Budget Manager</h2>

				<div class="ctrls">
	        <ul class="filters">
	          <li>
	            <a
	              href="#"
	              @click.prevent="changeFilter('all')"
	              class="filter"
	              :class="{ active: filter == 'all' }"
	            >All Clients</a>
	          </li>
	          <li>
	            <a
	              href="#"
	              @click.prevent="changeFilter('user')"
	              class="filter"
	              :class="{ active: filter == 'user' }"
	            >My Clients</a>
	          </li>
	        </ul>
					<ul class="actions">
	          <li>
	            <a @click.prevent="exportSummary()" href="#" class="action">Export to CSV</a>
	          </li>
	        </ul>
				</div>
      </div>

      <div class="panel-content">
        <div class="search">
          <div class="row">
            <div class="col">
              <div class="form-control-group">
                <div class="form-control">
                  <label for="year">Year</label>
                  <select v-model="year" name="year" id="year">
                    <option :value="currentYear - 2">{{ currentYear - 2 }}</option>
                    <option :value="currentYear - 1">{{ currentYear - 1 }}</option>
                    <option :value="currentYear">{{ currentYear }}</option>
                    <option :value="parseInt(currentYear) + 1">{{ parseInt(currentYear) + 1 }}</option>
                  </select>
                </div>
                <div class="form-control">
                  <label for="month">Month</label>
                  <select v-model="month" name="month" id="month">
                    <option v-for="(month, i) in months" :key="i" :value="i+1">{{ month }}</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-show="filter == 'all'" class="col">
              <label for="client-list">Client List</label>
              <select v-model="listId" id="client-list">
                <option value="0">All</option>
                <option v-for="list in lists" :key="list.id" :value="list.id">{{ list.name }}</option>
              </select>
              <router-link
                :to="{ name: 'ListIndex' }"
                class="client-list-edit"
              >add / remove client lists</router-link>
            </div>

            <div v-show="filter == 'user'" class="col">
              <label for="user">Filter By User</label>
              <select v-model="userId" name="user" id="user">
                <option value="0">Me</option>
                <option
                  v-for="user in users"
                  :key="user.id"
                  :value="user.id"
                >{{ user.first_name + ' ' + user.last_name }}</option>
              </select>
            </div>
          </div>

          <div class="form-control">
            <label for="client-search">Client Search</label>
            <input
              @input="debounceInput($event)"
              type="text"
              name="client_search"
              id="client-search"
              placeholder="Enter client name..."
            />
          </div>

          <div id="zero-switch">
            <label class="switch">
              <input v-model="showZero" type="checkbox" />
              <span class="slider"></span>
            </label>
            <span class="switch-text">Show $0 Investments</span>
          </div>
        </div>

        <div class="status-message">
          <div class="message">{{note}}</div>
          <a @click.prevent="showNoteEdit = true" href="#" class="note-update">update</a>
        </div>

        <loading v-if="loading"></loading>
        <div v-else class="clients table">
          <div ref="stickyheader" class="header table-row">
            <div class="table-cell"></div>
            <div class="table-cell client-name">Client</div>
            <div class="table-cell">Investment</div>
            <div class="table-cell">Management</div>
            <div class="table-cell">Call Tracking</div>
            <div class="table-cell">Click Budget</div>
            <div class="table-cell">Allotted</div>
            <div class="table-cell">Remaining</div>
            <div class="table-cell">Prev Month</div>
            <div class="table-cell">Balance</div>
          </div>

          <router-link
            v-for="budget in filteredBudgets"
            :key="budget.id"
            :to="{ name: 'BudgetShow', params: { clientId: budget.client_id } }"
            class="client table-row"
            :id="budget.client_id"
          >
            <div class="table-cell">
              <a
                @mouseenter="showInfo($event, budget)"
                @mouseleave="hideInfo()"
                class="info-button"
                href="#"
              >
                <i class="fa fa-info"></i>
              </a>
            </div>
            <div class="table-cell client-name">
              <div class="name">{{ budget.name }}</div>
              <ul class="flags">
                <li v-if="budget.client.client_card">Client Card</li>
                <li v-if="budget.client.zero_balance">Zero Balance</li>
                <li v-if="budget.client.is_using_google_post_pay">G Post-Pay</li>
              </ul>
              <ul class="notes">
                <li v-if="budget.cancellation" class="cancellation">Cancellation</li>
                <li v-if="budget.billing_hold" class="billing-hold">Billing Hold</li>
                <li v-if="budget.followup" class="followup">Followup</li>
              </ul>
            </div>
            <div
              class="table-cell"
            >${{ budget.investment_override ? budget.investment_override.toLocaleString() : budget.investment.toLocaleString() }}</div>
            <div
              class="table-cell"
            >${{ budget.management_fee_override ? budget.management_fee_override.toLocaleString() : budget.management_fee.toLocaleString() }}</div>
            <div class="table-cell">${{ budget.call_tracking_fee }}</div>
            <div class="table-cell">${{ budget.click_budget.toLocaleString() }}</div>
            <div
              class="table-cell"
              :class="{ 'highlight-yellow': budget.allotted == 0 }"
            >${{ budget.allotted.toLocaleString() }}</div>

            <div
              class="table-cell"
              :class="{ match: !budget.client.zero_balance ? checkMatch(budget) : budget.remaining == 0 }"
            >${{ budget.remaining.toLocaleString() }}</div>

            <div
              class="table-cell"
              :class="{
                zero: budget.previous == 0,
                match: !budget.client.zero_balance ? checkMatch(budget) : budget.previous == 0,
                over: !budget.client.zero_balance ? checkOver(budget) : budget.previous > 0,
                under: !budget.client.zero_balance ? checkUnder(budget) : budget.previous < 0 }"
            >${{ budget.previous.toLocaleString() }}</div> <!-- <<<<< -->
            <div
              v-if="budget.client.zero_balance"
              class="table-cell"
              :class="{ match: budget.remaining == 0 }"
            >${{ budget.remaining.toLocaleString() }}</div>
            <div
              v-else
              class="table-cell"
              :class="{ match: checkMatch(budget) }"
            >${{ (budget.previous + budget.remaining).toLocaleString() }}</div>
          </router-link>
        </div>
        <div class="table"></div>

        <table v-if="!loading" class="totals">
          <thead>
            <tr>
              <th>Source</th>
              <th>Total Investment</th>
              <th>Management</th>
              <th>Call Tracking</th>
              <th>Click Budget</th>
              <th>Total Allotted</th>
              <th>Remaining Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Total</td>
              <td>${{ total.investment.toLocaleString() }}</td>
              <td>${{ total.management.toLocaleString() }}</td>
              <td>${{ total.call_tracking.toLocaleString() }}</td>
              <td>${{ total.click_budget.toLocaleString() }}</td>
              <td>${{ total.allotted.toLocaleString() }}</td>
              <td>${{ total.remaining.toLocaleString() }}</td>
            </tr>
            <tr>
              <td>Trimark Card</td>
              <td>${{ trimarkCard.investment.toLocaleString() }}</td>
              <td>${{ trimarkCard.management.toLocaleString() }}</td>
              <td>${{ trimarkCard.call_tracking.toLocaleString() }}</td>
              <td>${{ trimarkCard.click_budget.toLocaleString() }}</td>
              <td>${{ trimarkCard.allotted.toLocaleString() }}</td>
              <td>${{ trimarkCard.remaining.toLocaleString() }}</td>
            </tr>
            <tr>
              <td>Client Card</td>
              <td>${{ clientCard.investment.toLocaleString() }}</td>
              <td>${{ clientCard.management.toLocaleString() }}</td>
              <td>${{ clientCard.call_tracking.toLocaleString() }}</td>
              <td>${{ clientCard.click_budget.toLocaleString() }}</td>
              <td>${{ clientCard.allotted.toLocaleString() }}</td>
              <td>${{ clientCard.remaining.toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>

        <router-link :to="{ name: 'LogReport' }" class="button button-orange">Log Report</router-link>
        <router-link :to="{ name: 'FundReport' }" class="button button-orange">Fund Report</router-link>
        <br />
        <button @click="exportFundReport()" class="button button-blue">Export Fund Report to CSV</button>
      </div>
    </div>

    <div ref="infoWindow" class="info-window" v-show="showInfoWindow">
      <h3>Funds</h3>
      <p v-if="infoWindow.funds.length == 0">
        <em>No funds have been allotted.</em>
      </p>
      <div v-else class="funds">
        <table>
          <thead>
            <tr>
              <th>Fund Type</th>
              <th>Allotted</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="fund in infoWindow.funds">
              <td>{{ fund.type }}</td>
              <td>${{ fund.allotted.toLocaleString() }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>Logs</h3>
      <p v-if="infoWindow.logs.length == 0">
        <em>No logs to show.</em>
      </p>
      <div v-else class="logs">
        <table>
          <thead>
            <tr>
              <th>Log Type</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="log in infoWindow.logs">
              <td>{{ log.type }}</td>
              <td>{{ log.description }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <note-edit
      :show="showNoteEdit"
      noteId="1"
      @update="getBudgetData()"
      @close="showNoteEdit = false"
    ></note-edit>
  </div>
</template>

<script>
import NoteEdit from "@/components/ui/NoteEdit";
import Loading from "@/components/ui/Loading";

import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import lodash from "lodash";

export default {
  name: "BudgetIndex",
  components: {
    NoteEdit,
    Loading
  },
  data() {
    return {
      loading: true,
      months: moment.months(),
      currentYear: moment().format("YYYY"),
      showZero: false,
      lists: [],
      budgets: [],
      users: [],
      client: null,
      note: null,
      showNoteEdit: false,
      showInfoWindow: false,
      infoWindow: {
        funds: [],
        logs: []
      }
    };
  },
  async created() {
    this.getBudgetData();
    let lists = await this.$http.get("/lists");
    this.lists = lists.data;
    let user = await this.$http.get("/auth/user");
    if (user.data.role.id <= 2) {
      this.changeFilter("all");
    }

    let users = await this.$http.get("/users");
    this.users = users.data;
  },
  mounted() {
    window.addEventListener("scroll", this.stickyHeader);
  },
  destroyed() {
    window.removeEventListener("scroll", this.stickyHeader);
  },
  computed: {
    ...mapGetters([
      "getYear",
      "getMonth",
      "getListId",
      "getUserId",
      "getFilter",
      "getUserId"
    ]),
    year: {
      get() {
        return this.getYear;
      },
      set(value) {
        this.setYear({ year: value });
      }
    },
    month: {
      get() {
        return this.getMonth;
      },
      set(value) {
        this.setMonth({ month: value });
      }
    },
    listId: {
      get() {
        return this.getListId;
      },
      set(value) {
        this.setListId({ listId: value });
      }
    },
    filter: {
      get() {
        return this.getFilter;
      },
      set(value) {
        this.setFilter({ filter: value });
      }
    },
    userId: {
      get() {
        return this.getUserId;
      },
      set(value) {
        this.setUserId({ userId: value });
      }
    },
    previousYear() {
      return +this.month == 1 ? +this.year - 1 : +this.year;
    },
    previousMonth() {
      return +this.month == 1 ? 12 : +this.month - 1;
    },
    filteredBudgets() {
      if (this.showZero) {
        return this.budgets;
      } else {
        return this.budgets.filter(budget => budget.investment > 0);
      }
    },
    total() {
      let totals = this.budgets.reduce(
        (total, budget) => {
          total.investment += budget.investment_override
            ? budget.investment_override
            : budget.investment;
          total.management += budget.management_fee;
          total.call_tracking += budget.call_tracking_fee;
          total.click_budget += budget.click_budget;
          total.allotted += budget.allotted;
          return total;
        },
        {
          investment: 0,
          management: 0,
          call_tracking: 0,
          click_budget: 0,
          allotted: 0
        }
      );

      totals.remaining = totals.click_budget - totals.allotted;
      return totals;
    },
    trimarkCard() {
      let totals = this.budgets.reduce(
        (total, budget) => {
          if (!budget.client.client_card) {
            total.investment += budget.investment_override
              ? budget.investment_override
              : budget.investment;
            total.management += budget.management_fee;
            total.call_tracking += budget.call_tracking_fee;
            total.click_budget += budget.click_budget;
            total.allotted += budget.allotted;
          }
          return total;
        },
        {
          investment: 0,
          management: 0,
          call_tracking: 0,
          click_budget: 0,
          allotted: 0
        }
      );

      totals.remaining = totals.click_budget - totals.allotted;
      return totals;
    },
    clientCard() {
      let totals = this.budgets.reduce(
        (total, budget) => {
          if (budget.client.client_card) {
            total.investment += budget.investment_override
              ? budget.investment_override
              : budget.investment;
            total.management += budget.management_fee;
            total.call_tracking += budget.call_tracking_fee;
            total.click_budget += budget.click_budget;
            total.allotted += budget.allotted;
          }
          return total;
        },
        {
          investment: 0,
          management: 0,
          call_tracking: 0,
          click_budget: 0,
          allotted: 0
        }
      );

      totals.remaining = totals.click_budget - totals.allotted;
      return totals;
    }
  },
  watch: {
    month() {
      this.getBudgetData();
    },
    year() {
      this.getBudgetData();
    },
    listId() {
      this.getBudgetData();
    },
    client() {
      this.getBudgetData();
    },
    userId(value) {
      this.getBudgetData();
    }
  },
  methods: {
    ...mapActions([
      "setYear",
      "setMonth",
      "setListId",
      "setUserId",
      "setFilter",
      "setUserId"
    ]),
    exportSummary() {
      this.$http
        .get("/paid-media-export/budgets", {
          params: {
            year: this.year,
            month: this.month,
            show_zero: this.showZero
          }
        })
        .then(response => {
          // Return CSV to browser
          var csvData = new Blob([response.data], {
            type: "text/csv;charset=utf-8;"
          });
          var link = document.createElement("a");
          link.href = window.URL.createObjectURL(csvData);
          link.setAttribute("download", "paid-budget-summary.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(error => {
          console.log(error);
        });
    },
    exportFundReport() {
      this.$http
        .get("/paid-media-export/funds", {
          params: { year: this.year, month: this.month }
        })
        .then(response => {
          // Return CSV to browser
          var csvData = new Blob([response.data], {
            type: "text/csv;charset=utf-8;"
          });
          var link = document.createElement("a");
          link.href = window.URL.createObjectURL(csvData);
          link.setAttribute("download", "fund-report.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(error => {
          console.log(error);
        });
    },
    async getBudgetData() {
      this.loading = true;

      let note = await this.$http.get("/global-notes/1");
      this.note = note.data.message;

      let previous = await this.$http.get("/paid-media-budgets", {
        params: {
          year: this.previousYear,
          month: this.previousMonth,
          listId: this.listId,
          filter: this.filter,
          client: this.client,
          userId: this.userId
        }
      });
      let previousBudgets = previous.data.map(budget => {
        budget.allotted = this.getFundingTotal(budget.funds);
        budget.remaining = budget.funds.reduce((total, fund) => {
          if (
            fund.type !== "Google Adwords" &&
            fund.type !== "Google Ads"
          ) {
            return total + ((fund.projected || 0) - (fund.actual || 0));
          }
          else {
            return total;
          }
        }, 0);
        return budget;
      });
      let current = await this.$http.get("/paid-media-budgets", {
        params: {
          year: this.year,
          month: this.month,
          listId: this.listId,
          filter: this.filter,
          client: this.client,
          userId: this.userId
        }
      });

      this.budgets = current.data.map(budget => {
        budget.allotted = this.getFundingTotal(budget.funds);
        if (budget.investment_override) {
          budget.remaining =
            budget.investment_override -
            (budget.management_fee_override ? budget.management_fee_override : budget.management_fee) -
            budget.call_tracking_fee -
            budget.allotted;
        } else {
          budget.remaining =
            budget.investment -
            budget.management_fee -
            budget.call_tracking_fee -
            budget.allotted;
        }

        let previousBudget = previousBudgets.find(
          previous => previous.client_id == budget.client_id
        );
        if (!previousBudget) {
          budget.previous = 0;
          return budget;
        }

        // Check for logs with status
        budget.client.paid_media_logs.forEach(log => {
          if (log.archived_by == null) {
            switch (log.type) {
              case "Followup":
                budget.followup = true;
                break;

              case "Billing Hold":
                budget.billing_hold = true;
                break;

              case "Cancellation":
                budget.cancellation = true;
                break;
            }
          }
        });

        budget.previous = previousBudget.remaining;
        return budget;
      });

      this.loading = false;
      this.$root.$emit('budgetDataLoaded');
    },
    changeFilter(filter) {
      this.filter = filter;
      this.getBudgetData();
    },
    getFundingTotal(funds) {
      return funds.reduce((carry, fund) => {
        if (fund.type == "Google Adwords" || fund.type == "Google Ads") {
          return carry + fund.actual;
        } else {
          return carry + fund.projected;
        }
      }, 0);
    },
    checkZero(budget) {
      return budget.previous == 0;
    },
    checkMatch(budget) {
      return budget.click_budget - budget.allotted + budget.previous == 0;
    },
    checkOver(budget) {
      return budget.previous > 0 && !this.checkMatch(budget);
    },
    checkUnder(budget) {
      return budget.previous < 0 && !this.checkMatch(budget);
    },
    debounceInput: _.debounce(function(e) {
      this.client = e.target.value;
    }, 300),
    stickyHeader(e) {
      if(this.$refs.stickyheader) {
        if (window.scrollY > this.$refs.stickyheader.offsetTop + 82) {
          let shift = window.scrollY - this.$refs.stickyheader.offsetTop - 82;
          this.$refs.stickyheader.style.transform = "translateY(" + shift + "px)";
        } else {
          this.$refs.stickyheader.style.transform = "translateY(0px)";
        }
      }
    },
    showInfo(e, budget) {
      let x = e.clientX;
      let y = e.clientY;
      this.$refs.infoWindow.style.top = y - 100 + "px";
      this.$refs.infoWindow.style.left = x + 50 + "px";

      this.infoWindow.logs = budget.client.paid_media_logs.filter(
        log => !log.archived_by
      );
      this.infoWindow.funds = budget.funds.reduce((total, item) => {
        let index = total.findIndex(fund => fund.type == item.type);
        if (index == -1) {
          total.push({
            type: item.type,
            allotted: item.type == "Google Adwords" || item.type == "Google Ads" ? item.actual : item.projected
          });

        } else {
          total[index].allotted += "Google Adwords" || item.type == "Google Ads" ? item.actual : item.projected;
        }
        return total;
      }, []);

      this.showInfoWindow = true;
    },
    hideInfo() {
      this.showInfoWindow = false;
    }
  }
};
</script>

<style lang="scss">
#budget-index {
  .search {
    background: lighten($light-gray, 3%);
    padding: 15px 30px;
    margin-bottom: 30px;
    border: 1px solid $light-gray;

    #client-list {
      margin-bottom: 0;
    }

    .client-list-edit {
      display: inline-block;
      margin-left: 5px;
      font-size: 12px;
    }

    #zero-switch {
      margin-top: 20px;
      font-size: 12px;
    }
  }

  .status-message {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 15px;
    margin-bottom: 15px;
    background: lighten($blue, 50%);
    border-radius: 4px;
    flex-direction: row;
    .message {
      flex: 1;
    }
    a {
      display: block;
      text-transform: uppercase;
      font-weight: 700;
    }
  }

  .note-update {
    display: block;
    float: right;
    padding: 0 5px;
    margin-bottom: 30px;
    font-size: 12px;
  }
  // Below is only because of sticky header
  .table {
    display: table;
    width: 100%;
    border-spacing: 0;
    border-collapse: collapse;
  }

  .table-row {
    display: table-row;
  }

  .table-cell {
    display: table-cell;
    padding: 5px 10px;
    vertical-align: middle;
    border-bottom: 1px solid $medium-gray;
    text-align: right;

    &.client-name {
      text-align: left;
      min-width: 250px;
    }
  }

  .header {
    font-weight: 700;
    font-size: 12px;
    background: $white;
  }

  .client {
    color: $black;

    &:nth-of-type(2n) {
      background: lighten($light-gray, 3%);
    }

    .name {
      font-weight: 700;
    }

    .highlight-yellow {
      background: #fcf7df;
    }

    .zero {
      background: #dbeed3;
    }

    .match {
      background: #cee7f5;
    }

    .over {
      background: #dbeed3;
    }

    .under {
      background: #f0d9d9;
    }

    &:hover {
      background: lighten($blue, 50%);
    }

    .info-button {
      color: $dark-gray;

      &:hover {
        color: $blue;
      }
    }

    .flags {
      display: flex;

      li {
        margin-right: 4px;
        margin-bottom: 4px;
        padding: 0 5px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: 700;
        color: $white;
        background: $blue;
      }
    }

    .notes {
      display: flex;

      li {
        margin-right: 4px;
        padding: 0 5px;
        border-radius: 3px;
        font-size: 10px;
        font-weight: 700;
        color: $white;
      }

      .followup {
        background: $green;
      }

      .billing-hold {
        background: $orange;
      }

      .cancellation {
        background: $red;
      }
    }
  }

  .totals {
    margin-top: 60px;

    td,
    th {
      padding: 4px 5px;
    }

    th {
      background: $medium-blue;
      color: $white;
      border: 1px solid $medium-blue;
      text-align: center;

      &:first-of-type {
        text-align: left;
      }
    }

    td {
      border: 1px solid $medium-gray;
      text-align: right;

      &:first-of-type {
        font-weight: 700;
        background: $light-gray;
        text-align: left;
      }
    }
  }

  .info-window {
    position: fixed;
    padding: 15px;
    border-radius: 5px;
    background: $white;
    box-shadow: 0 2px 4px 0 $medium-gray;
    min-width: 480px;

    h3 {
      font-size: 18px;
      margin-top: 20px;
      color: $blue;

      &:first-of-type {
        margin: 0;
      }
    }

    th {
      background: $medium-gray;
    }

    td,
    th {
      border: 1px solid $light-gray;
    }
  }
}
</style>
